import React, { useEffect, useState } from 'react';
import { motion } from "framer-motion";
import { Box, Typography, CircularProgress, Modal, TextField, Button } from "@mui/material";
import axios from 'axios';
import { loadScript, getInnerHTML } from '../../helpers/utility';
import DatalyzeLogo from "../../assets/img/dataLyze-new.svg"
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import "./SubscriptionPage.css";

const SubscriptionPage = () => {
    let rzpKey = process.env.REACT_APP_RAZORPAY_KEY;
    let paymentServiceApi = process.env.REACT_APP_PAYMENT_SERVICE;
    const userDetails = useSelector((state) => state?.user?.userDetails);
    const jwt = useSelector((state) => state?.user?.jwt);
    const navigate = useNavigate();
    const [loadingSubscriptionPlans, setLoadingSubscriptionPlans] = useState(false);
    const [loadingCreatePayment, setLoadingCreatePayment] = useState(null);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [openPhoneModal, setOpenPhoneModal] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [phoneError, setPhoneError] = useState('');

    const fetchSubscriptionList = async () => {
        setLoadingSubscriptionPlans(true)
        try {
            const response = await axios.get(
                `${paymentServiceApi}/subscriptions/plans`,
                {
                    headers: {
                        Authorization: `Bearer ${jwt}`,
                    },
                }
            );
            setSubscriptionPlans(response?.data?.data)
            setLoadingSubscriptionPlans(false)
            return response.data;
        } catch (error) {
            setLoadingSubscriptionPlans(false)
            console.error('Error fetching subscription plans:', error);
            return null;
        }
    };

    const validatePhoneNumber = (phone) => {
        const phoneRegex = /^[0-9]{10}$/;
        return phoneRegex.test(phone);
    };

    const handlePhoneSubmit = () => {
        if (!validatePhoneNumber(phoneNumber)) {
            setPhoneError('Please enter a valid 10-digit phone number');
            return;
        }
        setPhoneError('');
        setOpenPhoneModal(false);
        handlePayment(selectedPlan, phoneNumber);
    };

    const initiatePayment = (plan) => {
        setSelectedPlan(plan);
        setOpenPhoneModal(true);
    };

    const handlePayment = async (plan, phone) => {
        const res = await loadScript(
            "https://checkout.razorpay.com/v1/checkout.js"
        );

        if (!res) {
            // alert("Razorpay SDK failed to load. Are you online?");
            return;
        }
        setLoadingCreatePayment(plan?.rzpPlanId)
        try {
            const response = await axios.post(
                `${paymentServiceApi}/subscriptions`,
                {
                    rzpPlanId: plan?.rzpPlanId,
                },
                {
                    headers: {
                        Authorization: `Bearer ${jwt}`,
                    },
                }
            );

            if (response?.data?.data?.rzpSubId) {
                const createdOrder = {
                    razorpay_subscription_id: response?.data?.data?.rzpSubId,
                    Name: userDetails.name,
                    Email: userDetails.email,
                    Mobile: phone,
                };

                const data = Object.assign(createdOrder, plan);
                initPayment(data);
            }
        } catch (err) {
            console.log('Err: ', err);
        }
    }

    const initPayment = (data) => {
        var options = {
            key: rzpKey,
            subscription_id: data?.razorpay_subscription_id,
            interval: data?.interval,
            name: data.name,
            description: getInnerHTML(data.description),
            image: DatalyzeLogo,
            handler: function (response) {
                checkPaymentSubscription(response)
            },
            prefill: {
                name: data.Name,
                email: data.Email,
                contact: data.Mobile,
            },
            theme: {
                color: "#F44D5E",
            },
            modal: {
                ondismiss: () => {
                    console.log("Payment Cancelled");
                },
            },
        };
        var rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
            checkPaymentFailureSubscription(response?.error?.metadata?.payment_id, data?.razorpay_subscription_id);
        });
        rzp.open();
    }

    const checkPaymentFailureSubscription = async (payment_id, razorpay_subscription_id) => {
        const requestData = {
            razorpay_subscription_id: razorpay_subscription_id,
            razorpay_payment_id: payment_id,
        };

        try {
            const response = await axios.post(
                `${paymentServiceApi}/subscriptions/callback/failure`, requestData, {
                headers: {
                    Authorization: `Bearer ${jwt}`
                }
            }
            );
            console.log(response);
        } catch (error) {
            console.error('Error fetching subscription plans:', error);
            return null;
        }
    }

    const checkPaymentSubscription = async (response) => {
        const requestData = {
            razorpay_subscription_id: response.razorpay_subscription_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
        };
        try {
            const response = await axios.post(
                `${paymentServiceApi}/subscriptions/callback/success`, requestData, {
                headers: {
                    Authorization: `Bearer ${jwt}`
                }
            }
            );
            if (response?.data?.message === "success") {
                setLoadingCreatePayment(null);
                setShowSuccessToast(true);
                setTimeout(() => {
                    setShowSuccessToast(false);
                    navigate('/gamelist')
                }, 2000)
            }
        } catch (error) {
            console.error('Error fetching subscription plans:', error);
            return null;
        }
    }

    useEffect(() => {
        fetchSubscriptionList();
    }, [jwt])

    if (loadingSubscriptionPlans) {
        return (
            <div className="subscription-container">
                <Typography
                    sx={{
                        fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                        color: "#FFF",
                        fontFamily: "Plus Jakarta Sans",
                        fontStyle: "normal",
                        fontWeight: 600,
                        lineHeight: "48px",
                        letterSpacing: "-0.5px",
                        textAlign: "center",
                        marginBottom: "2rem"
                    }}
                >
                    Loading Plans...
                </Typography>
            </div>
        );
    }

    return (
        <div className="subscription-container">
            <Typography
                sx={{
                    fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                    color: "#FFF",
                    fontFamily: "Plus Jakarta Sans",
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "48px",
                    letterSpacing: "-0.5px",
                    textAlign: "center",
                    marginBottom: "2rem"
                }}
            >
                Choose Your Plan
            </Typography>

            <Box sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 4,
                justifyContent: "center",
                alignItems: "center",
                padding: "2rem"
            }}>
                {subscriptionPlans?.map((plan, index) => (
                    <motion.div
                        key={plan._id}
                        className="subscription-card"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.2 }}
                        whileHover={{
                            scale: 1.05,
                            transition: { duration: 0.2 }
                        }}
                    >
                        <div className="card-content">
                            <h2 className="plan-name">{plan.name.charAt(0).toUpperCase() + plan.name.slice(1)}</h2>
                            <div
                                className="plan-description"
                                dangerouslySetInnerHTML={{ __html: plan?.description }}
                            />
                            <p className="plan-short-description">{plan?.shortDescription}</p>

                            <div className="price-container">
                                <span className="actual-price" data-currency={plan?.currency}>{plan?.actualPrice}</span>
                                <span className="discounted-price" data-currency={plan?.currency}>{plan?.amount}</span>
                            </div>
                            <motion.button
                                className="subscribe-button"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => initiatePayment(plan)}
                                disabled={loadingCreatePayment === plan?.rzpPlanId}
                            >
                                {loadingCreatePayment === plan?.rzpPlanId ? (
                                    <CircularProgress size={24} sx={{ color: '#000' }} />
                                ) : "Subscribe Now"}
                            </motion.button>
                        </div>
                    </motion.div>
                ))}
            </Box>

            <Modal
                open={openPhoneModal}
                onClose={() => setOpenPhoneModal(false)}
                aria-labelledby="phone-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box sx={{
                    position: 'relative',
                    width: 400,
                    bgcolor: '#010E14',
                    borderRadius: '16px',
                    boxShadow: 24,
                    p: 4,
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                }}>
                    <Typography
                        sx={{
                            color: '#FFF',
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            mb: 3,
                            textAlign: 'center',
                        }}
                    >
                        Enter Your Phone Number
                    </Typography>
                    <TextField
                        fullWidth
                        label="Phone Number"
                        variant="outlined"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        error={!!phoneError}
                        helperText={phoneError}
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                color: '#FFF',
                                '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.23)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#00FF85',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                            },
                            '& .MuiFormHelperText-root': {
                                color: '#FF6B6B',
                            },
                        }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handlePhoneSubmit}
                        sx={{
                            background: 'linear-gradient(91deg, #00FF85 7.38%, #00E5FF 96.85%)',
                            color: '#000',
                            fontWeight: 600,
                            '&:hover': {
                                background: 'linear-gradient(91deg, #00E5FF 7.38%, #00FF85 96.85%)',
                            },
                        }}
                    >
                        Continue to Payment
                    </Button>
                </Box>
            </Modal>

            {showSuccessToast && <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 50, opacity: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                style={{
                    position: "fixed",
                    bottom: "10%",
                    left: "40%",
                    transform: "translate(-30%, -50%)",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: "40px",
                    width: "300px",
                    backgroundColor: "#2daf00",
                    borderRadius: "8px",
                    boxShadow: "0px 16px 24px 0px rgba(0, 0, 0, 0.14)",
                    padding: "10px 16px",
                    zIndex: 1000,
                }}
            >
                <p style={{
                    color: "#FFF",
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontSize: "16px",
                    fontWeight: 500,
                    lineHeight: "24px",
                    letterSpacing: "-0.5px",
                    margin: 0,
                }}>
                    Payment Successful
                </p>

                <button
                    onClick={() => {
                        setShowSuccessToast(false);
                        navigate('/gamelist')
                    }}
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "#FFF",
                        fontSize: "18px",
                        cursor: "pointer",
                        marginLeft: "12px",
                    }}
                >
                    ×
                </button>
            </motion.div>}
        </div>
    );
};

export default SubscriptionPage;